import { Box, Divider, Grid, Theme, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import * as React from "react";
import { useEffect } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { fetchData } from "../../common/services/ApiClient";
import {
  appTitles,
  fileNameFormat,
  formatDate,
  setInstitutionStatus,
} from "../../common/services/Utilities";
import { ApiBaseUrl } from "../../config";
import BackButton from "../../containers/BackButton";
import BankFindDetailsMenu from "../../containers/BankFindDetailsMenu";
import Breadcrumbs from "../../containers/Breadcrumbs";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";
import ReturnToLandingPageButton from "../../containers/ReturnToLandingPageButton";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import { Query, ResultRecord } from "../../interfaces";
import { selectedStatesAlpha } from "../../refdata/FilterOptions";
import ApiDataDownload, { apiDownloadProps } from "../ApiDataDownload";
import HelpButton from "../HelpContent/HelpButton";

interface Props {
  appState: Query;
  updateLocations: (city: string, state: string, county: string) => void;
}
interface LocationsByStateProps {
  count: number;
  STALP: string;
}

interface SucceedingInstitution {
  resultData: ResultRecord[];
  display: boolean;
  primaryRegulator: string;
}
const useClasses = makeStyles((theme: Theme) => ({
  root: {
    "& div.MuiTypography-body1": { lineHeight: "1rem" },
    "display": "inline-block",
    "& button#return-to-landing-page-button": {
      "margin-bottom": "2rem",
    },
    "& a": {
      textTransform: "none",
    },
    "width": "100%",
  },
  subHeader: {
    "display": "flex",
    "position": "relative",
    "& > p ": {
      color: theme.palette.primary.main,
      position: "absolute",
      right: 0,
    },
  },
  gridItem: {
    "& > div": {
      paddingBottom: "1rem",
    },
  },
  succeedingInstInfo: {
    lineHeight: "1rem",
    marginBottom: "1rem",
  },
  tooltipPopper: {
    maxWidth: "100%",
  },
  condensedTooltip: {
    maxWidth: 300,
  },
}));

export default function BankFindDetails({ appState, updateLocations }: Props) {
  const history = useHistory();
  const location = useLocation();
  const [bankFindDetails, setBankFindDetails] = React.useState<ResultRecord>();
  const [timeStamp, setTimeStamp] = React.useState<string>("");
  const [stateTerritories, setStateTerritories] = React.useState({
    resultData: [],
    count: 0,
  });

  let { cert } = useParams();

  if (appState.filters.cert && !cert) {
    cert = appState.filters.cert;
  }
  const name = bankFindDetails ? bankFindDetails.NAME : "";

  React.useEffect(() => {
    if (
      history.location.state &&
      history.location.state.fromOSCR &&
      (history.location.state.locationCity !== appState.search.city ||
        history.location.state.locationState !== appState.filters.stalp)
    ) {
      updateLocations(
        history.location.state.locationCity,
        history.location.state.locationState,
        history.location.state.locationCounty,
      );
    }
  }, [
    appState.filters.stalp,
    appState.filters.county,
    appState.search.city,
    history.location.state,
    updateLocations,
  ]);
  const apiData: apiDownloadProps[] = [
    {
      appState: {
        ...appState,
        filters: { cert },
        search: {},
      },
      fileName: `${fileNameFormat("History", cert, name)}.csv`,
      apiPath: "history",
      downloadAll: true,
      text: "Download All Institution Data",
      headers: [
        { label: "Effective Date", key: "EFFDATE" },
        { label: "Change Code", key: "CHANGECODE" },
        {
          label: "Change Code Description",
          key: "CHANGECODE_DESC_LONG",
        },
      ],
    },
    {
      appState: {
        ...appState,
        filters: { cert },
        search: {},
      },
      fileName: `${fileNameFormat("Institutions", cert, name)}.csv`,
      apiPath: "institutions",
      text: "Download Institution Details",
    },
    {
      appState: {
        ...appState,
        filters: {
          cert,
          stalp: appState.filters.stalp,
          zip: appState.filters.zip,
        },
        search: { city: appState.search.city },
      },
      fileName: `${fileNameFormat("Locations", cert, name)}.csv`,
      apiPath: "locations",
      text: "Download Locations Data",
    },
    {
      appState,
      fileName: `${fileNameFormat("History", cert, name)}.csv`,
      apiPath: "history",
      text: "Download Institution History",
      headers: [
        { label: "Effective Date", key: "EFFDATE" },
        { label: "Change Code", key: "CHANGECODE" },
        {
          label: "Change Code Description",
          key: "CHANGECODE_DESC_LONG",
        },
      ],
    },
  ];
  const [succeedingInst, setSucceedingInst] = React.useState<
    SucceedingInstitution
  >({
    resultData: [],
    display: false,
    primaryRegulator: "",
  });

  let stateCount = 0;
  let territoriesCount = 0;
  stateTerritories.resultData.forEach((data: LocationsByStateProps) => {
    return selectedStatesAlpha.map(state => {
      return state.value === data.STALP
        ? state.isState
          ? (stateCount = stateCount + 1)
          : (territoriesCount = territoriesCount + 1)
        : 0;
    });
  });

  if (
    succeedingInst.display &&
    history.location.state &&
    history.location.state.from === "succeedingInstitutions"
  ) {
    setSucceedingInst({ ...succeedingInst, display: false });
  }

  useEffect(() => {
    let isMounted = true;
    (async () => {
      await fetchData(`${ApiBaseUrl()}/institutions?filters=CERT:${cert}`)
        .then(response => {
          if (isMounted) {
            setBankFindDetails(
              response.data[0]
                ? setInstitutionStatus(response.data[0])
                : undefined,
            );
            setTimeStamp(response.createTimestamp);
          }
        })
        .catch((error: Error) => {
          console.error(`${error.name} : ${error.message}`);
        });
    })();
    return () => {
      isMounted = false;
    };
  }, [cert]);

  useEffect(() => {
    if (
      bankFindDetails &&
      bankFindDetails.ULTCERT &&
      bankFindDetails.ULTCERT.toString() !== bankFindDetails.CERT.toString()
    ) {
      let isMounted = true;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/institutions?filters=CERT:${
            bankFindDetails.ULTCERT
          }`,
        )
          .then(response => {
            if (response.data.length > 0 && isMounted) {
              setSucceedingInst({
                resultData: response.data.map((data: any) =>
                  setInstitutionStatus(data),
                ),
                display: true,
                primaryRegulator: response.data[0].REGAGNT,
              });
            }
          })
          .catch((error: Error) => {
            console.error(`${error.name} : ${error.message}`);
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [bankFindDetails]);

  useEffect(() => {
    window.scrollTo(0, 0);
    let isMounted = true;
    if (bankFindDetails && bankFindDetails.ACTIVE) {
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/locations?filters=CERT:${
            bankFindDetails.CERT
          }&agg_by=STALP`,
        )
          .then(response => {
            if (isMounted) {
              setStateTerritories({
                resultData: response.data,
                count: response.totals.count,
              });
            }
          })
          .catch((error: Error) => {
            console.error(`${error.name} : ${error.message}`);
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [bankFindDetails]);

  const classes = useClasses();

  useInitialPageLoad("bankFindDetails");

  return bankFindDetails ? (
    <>
      <Grid container spacing={2}>
        <Grid item xs={11}>
          <Breadcrumbs currentPageBreadCrumbTitle={appTitles("bankFind")} />
        </Grid>
        <Grid item xs={1}>
          <HelpButton />
        </Grid>
      </Grid>
      <CloudFoundryBanner />
      <div className={classes.root} id={"bank-detail"}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={succeedingInst.display ? 9 : 12} lg={12}>
            <>
              <ReturnToLandingPageButton />
              <BackButton
                app="BankFind"
                buttonLabel="Back to Search Results"
                variant="dark"
              />
            </>
            <Box>
              <Typography
                id="inst-name-header"
                variant={"h1"}
                marginBottom={".5rem"}
              >
                {bankFindDetails.NAME}
              </Typography>
              <Box position="relative">
                <ApiDataDownload
                  app="bankFindDetails"
                  apiPathLink="institutions"
                  apiData={apiData}
                  filters={[bankFindDetails.CERT, bankFindDetails.ULTCERT]}
                  name={bankFindDetails.NAME}
                  cert={bankFindDetails.CERT}
                />
              </Box>
            </Box>

            <Divider variant="fullWidth" />

            <Typography
              textAlign="right"
              id={"data-as-of-date"}
              fontSize={16}
              color="primary"
              marginTop={2}
              marginBottom={2}
              lineHeight={"1rem"}
            >
              Data as of {formatDate(timeStamp)}
            </Typography>

            <BankFindDetailsMenu
              results={bankFindDetails}
              location={location}
            />
          </Grid>
        </Grid>
      </div>
    </>
  ) : (
    <></>
  );
}
